/**
 * @file The sidebar: workspace switcher, grouped navigation.
 *
 * Rendered twice — once in the fixed desktop rail, once inside the mobile Sheet —
 * from the same component and the same `nav.ts` map, so the two cannot drift.
 *
 * **Everything clickable is a real `<Link>`.** The previous screens made rows
 * `<button>`s, which meant no middle-click, no "open in new tab", no copying a
 * link from the context menu, and no destination preview on hover — the routing
 * existed but nothing used it. Any future nav row should be a link for the same
 * reason.
 *
 * The "Soon" badge is a statement of intent, not decoration: those items have no
 * route and are not interactive, so the first click never teaches a landlord that
 * the app is broken.
 */
import { Link } from "@tanstack/react-router";
import { ChevronsUpDown } from "lucide-react";
import { NAV_GROUPS } from "../nav";
import type { ApiWorkspace } from "../api/types";
import { useMe } from "../api/queries";
import { Badge } from "./ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { cn } from "cn";

/**
 * Which workspace you are looking at, and how to change it.
 *
 * The switcher only appears when there is more than one. A landlord with a single
 * portfolio — the common case, and the reason the old mandatory picker screen was
 * wrong — should never be asked a question with one answer.
 */
function WorkspaceSwitcher({ workspace }: { readonly workspace: ApiWorkspace }) {
  const me = useMe();
  const others = (me.data?.workspaces ?? []).filter((w) => w.id !== workspace.id);

  const label = (
    <>
      <span className="grid size-8 shrink-0 place-items-center rounded-md bg-primary/10 text-xs font-semibold text-primary">
        {workspace.name.slice(0, 2).toUpperCase()}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{workspace.name}</span>
        <span className="block truncate text-xs capitalize text-muted-foreground">
          {workspace.role}
        </span>
      </span>
    </>
  );

  if (others.length === 0) {
    return <div className="flex items-center gap-2 px-2 py-2">{label}</div>;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left hover:bg-muted">
        {label}
        <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>Switch portfolio</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {others.map((other) => (
          <DropdownMenuItem key={other.id} asChild>
            <Link to="/workspaces/$workspaceId" params={{ workspaceId: other.id }}>
              <span className="truncate">{other.name}</span>
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AppSidebar({
  workspace,
  onNavigate,
}: {
  readonly workspace: ApiWorkspace;
  /** Called after a link is followed, so the mobile drawer can close itself. */
  readonly onNavigate?: () => void;
}) {
  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-3">
      <WorkspaceSwitcher workspace={workspace} />

      <nav aria-label="Sections" className="flex flex-col gap-4">
        {NAV_GROUPS.map((group) => (
          <div key={group.label}>
            <p className="px-2 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {group.label}
            </p>
            <ul className="flex flex-col gap-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                if (item.kind === "soon") {
                  return (
                    <li key={item.label}>
                      {/* Not a <Link> and not focusable: there is no page yet. */}
                      <span
                        aria-disabled="true"
                        className="flex items-center gap-2 rounded-md px-2 py-2 text-sm text-muted-foreground"
                      >
                        <Icon className="size-4 shrink-0" aria-hidden="true" />
                        <span className="flex-1 truncate">{item.label}</span>
                        <Badge variant="secondary" className="text-[10px]">
                          Soon
                        </Badge>
                      </span>
                    </li>
                  );
                }
                return (
                  <li key={item.label}>
                    <Link
                      to={item.to}
                      params={{ workspaceId: workspace.id }}
                      onClick={onNavigate}
                      // The dashboard is the layout's index route, so it would
                      // otherwise stay "active" on every child page.
                      activeOptions={{ exact: item.to === "/workspaces/$workspaceId" }}
                      className={cn(
                        "flex items-center gap-2 rounded-md px-2 py-2 text-sm",
                        "text-foreground hover:bg-muted",
                      )}
                      activeProps={{ className: "bg-primary/10 font-medium text-primary" }}
                    >
                      <Icon className="size-4 shrink-0" aria-hidden="true" />
                      <span className="flex-1 truncate">{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
    </div>
  );
}
